import { api, unwrap } from '@saas-kit/lib';

import type { TokenPrintResult } from './apiPrint';
import type { QmsToken } from './types';

export interface KioskService {
  id: string;
  name: string;
  name_ar: string | null;
}

export interface KioskInfo {
  tenant_name: string;
  logo_url: string | null;
  header_text: string | null;
  footer_text: string | null;
  services: KioskService[];
}

/** Raw kiosk feed: the backend reports the logo as a flag, not a URL. */
interface KioskInfoResponse {
  tenant_name: string;
  has_logo: boolean;
  header_text: string | null;
  footer_text: string | null;
  services: KioskService[];
}

export interface KioskLink {
  token: string;
  expires_at: number;
}

/** Same-origin URL the kiosk logo is streamed from. Routing the image
 *  through our own API (rather than a cross-origin storage URL) lets the
 *  token "save as picture" draw it onto a canvas without tripping CORS. */
function kioskLogoUrl(token: string): string {
  const base = api.defaults.baseURL ?? '';
  return `${base}/qms/public/kiosk/logo?t=${encodeURIComponent(token)}`;
}

/** Public kiosk feed — the tenant is resolved from the signed `t` token. */
export async function fetchKioskInfo(token: string): Promise<KioskInfo> {
  const info = await unwrap<KioskInfoResponse>(
    api.get('/qms/public/kiosk', { params: { t: token } }),
  );
  return {
    tenant_name: info.tenant_name,
    logo_url: info.has_logo ? kioskLogoUrl(token) : null,
    header_text: info.header_text,
    footer_text: info.footer_text,
    services: info.services,
  };
}

/** Issue a self-service token for a chosen service from the public kiosk.
 *  The customer name and phone are optional — a walk-in can issue a token
 *  without identifying themselves. */
export async function issueKioskToken(
  token: string,
  serviceId: string,
  customerName: string | null,
  customerPhone: string | null,
): Promise<QmsToken> {
  return unwrap<QmsToken>(
    api.post(
      '/qms/public/kiosk/tokens',
      {
        service_id: serviceId,
        customer_name: customerName,
        customer_phone: customerPhone,
      },
      { params: { t: token } },
    ),
  );
}

/** Render a kiosk-issued token to ESC/POS. With the `agent` transport the
 *  backend pushes the receipt to the tenant's print agent; the signed `t`
 *  token scopes the request to its tenant — matching the issue flow. */
export async function printKioskToken(
  token: string,
  tokenId: string,
): Promise<TokenPrintResult> {
  return unwrap<TokenPrintResult>(
    api.post(`/qms/public/kiosk/tokens/${tokenId}/print`, null, {
      params: { t: token },
    }),
  );
}

/** Owner-facing: mint a fresh signed kiosk token to build the printable QR. */
export async function fetchKioskLink(): Promise<KioskLink> {
  return unwrap<KioskLink>(api.get('/qms/kiosk-link'));
}
